package com.example.mystybag.appCore.views

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.HorizontalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.style.TextAlign
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p05
import com.example.mystybag.core.ui.theme.LocalTheme.p10
import com.example.mystybag.core.ui.theme.LocalTheme.p100
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.ui.theme.LocalTheme.p44
import com.example.mystybag.core.ui.theme.LocalTheme.p5
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.LazyShimmerColumn
import com.example.mystybag.common.extensions.roundedContainer
import com.example.mystybag.common.extensions.shimmer
import com.example.mystybag.common.model.IconData
import com.example.mystybag.common.ui.components.custom.CircleIconView
import com.example.mystybag.common.ui.components.custom.NavigationTabItem
import com.example.mystybag.common.ui.components.custom.SearchTextField
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.common.views.DateRangePickerDialog
import com.example.mystybag.appCore.logic.NotificationsViewModel

@Composable
fun NotificationsView(viewModel: NotificationsViewModel) {
    UIView(
        modifier                = Modifier.padding(horizontal = p16, vertical = p8),
        viewModel               = viewModel,
        removeVerticalPadding   = true
    ) { uiState ->
        Column {
            Row(
                modifier            = Modifier.padding(top = p16, bottom = p12).fillMaxWidth(),
                verticalAlignment   = Alignment.CenterVertically
            ) {
                SearchTextField(
                    modifier = Modifier
                        .weight(1f)
                        .padding(end = p16),
                    searchHint      = sharedString(SR.strings.search),
                    searchCallbacks = remember { viewModel.searchControls },
                    maxLines        = 1,
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences)
                )

                CircleIconView(
                    model   = IconData(
                        circleSize      = p44,
                        iconSize        = p16,
                        circleColor     = if (uiState.value.selectedStartDate != null) AppTheme.current.colors.primaryContainer else AppTheme.current.colors.surface,
                        iconColor       = if (uiState.value.selectedStartDate != null) AppTheme.current.colors.onPrimaryContainer else AppTheme.current.colors.onSurface,
                        icon            = painterResource(SRC.images.filter_icon)
                    ),
                    onClick = viewModel::onFilterButtonClicked
                )
            }

            uiState.value.filteredDates?.let {
                NavigationTabItem(
                    modifier        = Modifier.padding(bottom = p12),
                    text            = it,
                    onCloseClick    = viewModel::onRemoveFiltrationClicked
                )
            }
        }

        if (uiState.value.notifications.isEmpty())
            Column(
                modifier            = Modifier.fillMaxSize(),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(p16, Alignment.CenterVertically)
            ) {
                Image(
                    modifier            = Modifier
                        .width(p100)
                        .aspectRatio(1f),
                    painter             = painterResource(SRC.images.no_results_found_image)
                )
                Texts.TitleSmall(stringResource(SR.strings.no_notifications_found))
            }
        else
            LazyShimmerColumn(
                isLoading           = uiState.value.isLoading,
                shimmerItem         = { isLoading -> notificationsShimmer(isLoading) },
                verticalArrangement = Arrangement.spacedBy(p12)
            ) {
                items(uiState.value.notifications) { notification ->
                    Column(modifier = Modifier.fillMaxWidth()) {
                        if (notification.shouldShowDate)
                            Texts.TitleSmall(
                                modifier    = Modifier.padding(top = p12, bottom = p8),
                                text        = notification.date
                            )

                        Column(
                            modifier            = Modifier
                                .fillMaxWidth()
                                .roundedContainer(p16),
                            verticalArrangement = Arrangement.spacedBy(p4)
                        ) {
                            if (notification.isEmail)
                                Texts.ClickableTextView(
                                    texts       = notification.emailTexts,
                                    textStyle   = AppTheme.current.typography.labelLarge,
                                    onItemClick = {
                                        viewModel.onEmailLinkClicked()
                                    }
                                )
                            else
                                Texts.LabelLarge(
                                    text        = notification.message,
                                    textAlign   = TextAlign.Justify
                                )
                            Texts.LabelLarge(
                                text    = notification.time,
                                color   = AppTheme.current.colors.onSurfaceVariant
                            )
                        }
                    }
                }
            }

        if (uiState.value.showDatePickerDialog)
            DateRangePickerDialog(
                selectableDates     = uiState.value.selectableDates,
                yearsList           = uiState.value.yearsList,
                selectedStartDate   = uiState.value.selectedStartDate,
                selectedEndDate     = uiState.value.selectedEndDate,
                onDismiss           = viewModel::dismissDialog,
                onPositiveClicked   = viewModel::onDateSelected
            )
    }
}

fun LazyListScope.notificationsShimmer(isLoading: Boolean) {
    repeat(3) {
        item {
            Column(modifier = Modifier.fillMaxWidth()) {
                Texts.BodyMedium(
                    modifier    = Modifier
                        .padding(top = p10, bottom = p5)
                        .shimmer(isLoading, width = .5f),
                    text        = "text"
                )
                Texts.BodyMedium(
                    modifier    = Modifier
                        .padding(top = p16, bottom = p8)
                        .shimmer(isLoading),
                    text        = "text \n text",
                    textAlign   = TextAlign.Justify
                )
                Texts.BodyMedium(
                    modifier    = Modifier
                        .padding(bottom = p5)
                        .fillMaxWidth(.4f)
                        .align(Alignment.End)
                        .shimmer(isLoading),
                    text        = "text",
                    textAlign   = TextAlign.End
                )
                HorizontalDivider(
                    modifier    = Modifier.fillMaxWidth(),
                    thickness   = p05,
                    color       = AppTheme.current.colors.surface,
                )
            }

        }
    }
}
